/**
 * Birthday parties is a CROSS-CUT guide. The venues already live on other
 * pages — Tucker's Grove is on Parks, the Zoo is on Attractions, SB
 * Gymnastics is on Classes — so this page does not duplicate them. It
 * gathers them under the one search parents actually make, and links back.
 *
 * It lives at /things-to-do/kids-birthday-party-places-santa-barbara, and
 * "Birthday parties" is a TAG on every venue so the list can never go stale.
 */

export const birthdaysIntro = {
  meta: '24 venues · Prices checked August 2026 · 8 min read',
  deck:
    'Every place in Santa Barbara that will host a kids’ party, with what it actually costs, how many kids it fits, and whether you are cleaning up afterwards. Sorted by the thing you are trying to solve.',
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/dbf65824-a848-4816-a8da-0bdc0d5c1069.jpg',
  heroAlt:
    'A picnic table under an oak tree set for a small birthday party with a homemade cake and a few balloons',
  author: 'Rachel',
  published: 'Updated August 2026',
}

export const shortAnswer = {
  title: 'The short answer',
  body: 'Reserve a group area at Tucker’s Grove for the cheapest good party — about $130, full shade, and room for forty people. For a hands-off party where someone else runs it and cleans up, SB Gymnastics and the Santa Barbara Zoo are the two most reliable, at roughly $400–$650. Book any venue eight to twelve weeks ahead for a spring or fall weekend; the county park group areas open ninety days out and the good ones go the first morning.',
  picks: [
    { label: 'Cheapest that still feels special', value: 'Tucker’s Grove group area' },
    { label: 'Least work for you', value: 'SB Gymnastics Club' },
    { label: 'Rain-proof', value: 'MOXI or Rockin’ Jump' },
    { label: 'Under-3s', value: 'Kids’ World + a picnic blanket' },
  ],
}

export interface PartyRoute {
  slug: string
  name: string
  budget: string
  effort: string
  blurb: string
  swatch: string
}

export const routes: PartyRoute[] = [
  {
    slug: 'park-birthday-parties-santa-barbara',
    name: 'Park & beach parties',
    budget: '$0–150',
    effort: 'You run it',
    blurb:
      'Reserve a group area, bring everything, keep it two hours. Cheapest by far and the best option under age four.',
    swatch: 'bg-foam',
  },
  {
    slug: 'party-venues-santa-barbara',
    name: 'Venues that host it for you',
    budget: '$350–800',
    effort: 'They run it',
    blurb:
      'A host, a set time block, tables set and cleared. You show up with a cake.',
    swatch: 'bg-peach',
  },
  {
    slug: 'indoor-birthday-parties-santa-barbara',
    name: 'Indoor & rain-proof',
    budget: '$400–700',
    effort: 'They run it',
    blurb:
      'The safe call between December and March, when an outdoor party is a coin flip.',
    swatch: 'bg-opal',
  },
  {
    slug: 'birthday-party-entertainers-santa-barbara',
    name: 'Entertainers who come to you',
    budget: '$200–450',
    effort: 'Shared',
    blurb:
      'Petting zoos, magicians, science shows and a bounce house in your own yard.',
    swatch: 'bg-creme',
  },
]

export interface Venue {
  id: string
  name: string
  type: string
  livesOn: string
  livesOnHref: string
  cost: string
  capacity: string
  duration: string
  ages: string
  cleanup: string
  blurb: string
  tags: string[]
}

export const venues: Venue[] = [
  {
    id: 'b1',
    name: 'Tucker’s Grove group areas',
    type: 'Park',
    livesOn: 'Parks & playgrounds',
    livesOnHref: '/parks',
    cost: '$130 reservation',
    capacity: 'Up to 100',
    duration: 'All day',
    ages: 'All',
    cleanup: 'You',
    blurb:
      'Full oak shade, tables already there, a creek and a playground doing the entertaining. Book ninety days out at the county parks site — Kiwanis Meadow is the one to ask for.',
    tags: ['Park', 'Outdoor', 'Budget', 'Big groups'],
  },
  {
    id: 'b2',
    name: 'SB Gymnastics Club',
    type: 'Gym',
    livesOn: 'Classes & sports',
    livesOnHref: '/classes',
    cost: '$425 / 2 hrs',
    capacity: '20 kids',
    duration: '2 hours',
    ages: '3–12',
    cleanup: 'Them',
    blurb:
      'Ninety minutes in the gym with two coaches running it, then thirty in the party room. The least work of any party we have thrown, and kids leave genuinely tired.',
    tags: ['Indoor', 'Hands-off', 'Big kids'],
  },
  {
    id: 'b3',
    name: 'Santa Barbara Zoo',
    type: 'Attraction',
    livesOn: 'Attractions',
    livesOnHref: '/attractions',
    cost: '$650 / 3 hrs',
    capacity: '25 guests',
    duration: '3 hours',
    ages: '2–10',
    cleanup: 'Them',
    blurb:
      'A private area on the hillside lawn, admission for every guest, and a keeper visit with an animal. Expensive, and the only party on this list adults enjoy too.',
    tags: ['Outdoor', 'Hands-off', 'Premium'],
  },
  {
    id: 'b4',
    name: 'MOXI',
    type: 'Museum',
    livesOn: 'Attractions',
    livesOnHref: '/attractions',
    cost: '$550 / 2 hrs',
    capacity: '20 kids',
    duration: '2 hours',
    ages: '4–11',
    cleanup: 'Them',
    blurb:
      'A dedicated party room plus run of the museum. The rain-proof choice, and the one that works when the guest list spans a wide age range.',
    tags: ['Indoor', 'Hands-off', 'Rain-proof', 'Big kids'],
  },
  {
    id: 'b5',
    name: 'Kids’ World at Alameda Park',
    type: 'Playground',
    livesOn: 'Parks & playgrounds',
    livesOnHref: '/parks',
    cost: 'Free',
    capacity: 'Bring a blanket',
    duration: 'Whatever you want',
    ages: '1–8',
    cleanup: 'You',
    blurb:
      'No reservation system, so it is first-come and you cannot hold a spot. Perfect for a small toddler party where six kids and a cake is the whole plan.',
    tags: ['Park', 'Outdoor', 'Budget', 'Toddlers'],
  },
  {
    id: 'b6',
    name: 'Rockin’ Jump',
    type: 'Trampoline park',
    livesOn: 'Attractions',
    livesOnHref: '/attractions',
    cost: '$400 / 2 hrs',
    capacity: '15 jumpers',
    duration: '2 hours',
    ages: '5–13',
    cleanup: 'Them',
    blurb:
      'Loud, indoor, and reliably a hit from about six up. Socks are an extra charge per kid, which adds up faster than you would expect.',
    tags: ['Indoor', 'Hands-off', 'Rain-proof', 'Big kids'],
  },
  {
    id: 'b7',
    name: 'Chase Palm Park carousel & Shipwreck',
    type: 'Park',
    livesOn: 'Parks & playgrounds',
    livesOnHref: '/parks',
    cost: '$3/ride + free park',
    capacity: 'Informal',
    duration: '2 hours',
    ages: '2–8',
    cleanup: 'You',
    blurb:
      'The playground is the party and the carousel is the activity. No reservations, so arrive early on a Saturday to claim a table.',
    tags: ['Park', 'Outdoor', 'Budget', 'Toddlers'],
  },
  {
    id: 'b8',
    name: 'Santa Barbara Public Library community room',
    type: 'Community',
    livesOn: 'Things to do',
    livesOnHref: '/things-to-do',
    cost: 'Free–$50',
    capacity: '30 people',
    duration: '2 hours',
    ages: 'All',
    cleanup: 'You',
    blurb:
      'Almost nobody knows you can book these. Indoor, free or nearly, and the best-value rain backup in town — but no decorations on the walls and you are out on the hour.',
    tags: ['Indoor', 'Budget', 'Rain-proof'],
  },
]

export const partyFilters = [
  { group: 'Type', options: ['Park', 'Indoor', 'Outdoor', 'Rain-proof'] },
  { group: 'Budget', options: ['Budget', 'Premium'] },
  { group: 'Effort', options: ['Hands-off'] },
  { group: 'Ages', options: ['Toddlers', 'Big kids'] },
]

export const timeline = [
  {
    when: '10–12 weeks out',
    what: 'Book the venue',
    detail:
      'County park group areas open ninety days ahead and the shaded ones go the first morning. Gyms and museums fill spring and fall weekends about eight weeks out.',
  },
  {
    when: '4 weeks out',
    what: 'Send invitations',
    detail:
      'Earlier than this and people forget. Later and you are competing with three other parties on the same Saturday.',
  },
  {
    when: '2 weeks out',
    what: 'Order the cake, confirm the count',
    detail:
      'Most venues want a final headcount a week ahead and will charge for the number you gave them, not the number who show.',
  },
  {
    when: 'The week of',
    what: 'Check the weather, have a plan B',
    detail:
      'December through March, know your indoor fallback before you need it. A library community room can usually be booked with a few days’ notice.',
  },
]

export const partiesFaqs = [
  {
    question: 'Where can you have a kids birthday party in Santa Barbara?',
    answer:
      'The cheapest good option is a reserved group area at a county park like Tucker’s Grove, at about $130 for the day with shade and tables included. For a hosted party, SB Gymnastics, MOXI, the Santa Barbara Zoo and Rockin’ Jump all run two- to three-hour packages between $400 and $650 that include setup and cleanup.',
  },
  {
    question: 'How much does a kids birthday party cost in Santa Barbara?',
    answer:
      'A park party runs $0–150 plus food. A hosted venue party runs $400–650 for fifteen to twenty-five kids. An entertainer who comes to your house is $200–450. Add roughly $8–12 a head for food and favors on top of any of those.',
  },
  {
    question: 'How far in advance should I book?',
    answer:
      'Eight to twelve weeks for a spring or fall weekend. County park group areas open exactly ninety days ahead and the shaded ones are gone within hours of opening.',
  },
  {
    question: 'What is the best birthday party for a two-year-old?',
    answer:
      'A playground and a cake. Kids’ World or Chase Palm Park, six kids, two hours, no venue at all. Under three, a structured party at a venue is mostly for the adults.',
  },
]

export const partiesTier3 = [
  {
    area: 'Entertainers who come to you',
    items: ['SB Party Animals petting zoo', 'Mad Science of SB', 'Bounce house rentals', 'Face painting & balloons'],
  },
  {
    area: 'Cakes & food',
    items: ['Renaud’s Patisserie', 'Crushcakes', 'Lilac Pâtisserie (gluten-free)', 'Pizza catering options'],
  },
  {
    area: 'More venues',
    items: ['Page Youth Center', 'SB Museum of Art studio', 'Art from Scrap workshops', 'Elings Park group areas'],
  },
  {
    area: 'Related guides',
    items: ['Best parks with group areas', 'Rainy day ideas', 'Indoor attractions', 'Beaches with kids'],
  },
]
